import type { ReactNode } from "react";

import type { MessageKey } from "@sirdab/returns-shared";

import { StepHeader } from "../components";
import { FLOW_STEPS, stepNumber, type FlowStepId } from "../flow";
import { useMessages } from "../i18n";

/** Short chip name per step, in `FLOW_STEPS` order (keys added alongside this feature). */
const STEP_LABEL_KEYS: Record<FlowStepId, MessageKey> = {
  lookup: "ui.step_label.lookup",
  items: "ui.step_label.items",
  reason: "ui.step_label.reason",
  evidence: "ui.step_label.evidence",
  resolution: "ui.step_label.resolution",
  review: "ui.step_label.review",
};

/**
 * The shared frame every step screen sits in. It owns the header and the column; the step
 * owns its body. A screen author should never need to touch routing, i18n, theming or the
 * API client to fill one in.
 */
export function StepScaffold({
  step,
  titleKey,
  descriptionKey,
  children,
}: {
  step: FlowStepId;
  titleKey: MessageKey;
  descriptionKey?: MessageKey | undefined;
  children: ReactNode;
}): React.JSX.Element {
  const { t } = useMessages();
  return (
    <section className="flex flex-col gap-6">
      <StepHeader
        step={stepNumber(step)}
        total={FLOW_STEPS.length}
        stepLabels={FLOW_STEPS.map((id) => t(STEP_LABEL_KEYS[id]))}
        title={t(titleKey)}
        {...(descriptionKey === undefined
          ? {}
          : { description: t(descriptionKey) })}
      />
      {children}
    </section>
  );
}
